import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, BarChart3, TrendingUp, Loader2, AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";

/* ---------- types ---------- */
interface FormData {
  /* basics */
  collections: string;
  goal: string;
  /* website */
  hasWebsite: string;
  websiteUrl: string;
  tracksConversions: string;
  /* google */
  hasGbp: string;
  reviewRange: string;
  postsOnGbp: string;
  /* ads */
  runningGoogle: string;
  googleBudget: string;
  runningMeta: string;
  metaBudget: string;
  /* leads */
  monthlyLeads: string;
  monthlyNewPatients: string;
  /* tracking */
  tracksCalls: string;
  tracksShowRate: string;
  /* other */
  hasDirectMail: string;
  hasSeo: string;
  /* intent */
  timeline: string;
  monthlyBudget: string;
  isDecisionMaker: string;
  whyNow: string;
  /* contact */
  name: string;
  email: string;
  phone: string;
}

const initial: FormData = {
  collections: "",
  goal: "",
  hasWebsite: "",
  websiteUrl: "",
  tracksConversions: "",
  hasGbp: "",
  reviewRange: "",
  postsOnGbp: "",
  runningGoogle: "",
  googleBudget: "",
  runningMeta: "",
  metaBudget: "",
  monthlyLeads: "",
  monthlyNewPatients: "",
  tracksCalls: "",
  tracksShowRate: "",
  hasDirectMail: "",
  hasSeo: "",
  timeline: "",
  monthlyBudget: "",
  isDecisionMaker: "",
  whyNow: "",
  name: "",
  email: "",
  phone: "",
};

/* ---------- scoring engine (0-100) ---------- */
interface ScoreBreakdown {
  website: number;
  google: number;
  paidAds: number;
  social: number;
  tracking: number;
  budgetVsProduction: number;
  total: number;
  grade: string;
}

function scoreApplication(d: FormData): ScoreBreakdown {
  let website = 0; // max 20
  let google = 0; // max 20
  let paidAds = 0; // max 20
  let social = 0; // max 10
  let tracking = 0; // max 20
  let budgetVsProduction = 0; // max 10

  // Website (0-20)
  if (d.hasWebsite === "yes") website += 10;
  if (d.tracksConversions === "yes") website += 10;

  // Google / SEO / Maps (0-20)
  if (d.hasGbp === "yes") google += 7;
  if (d.postsOnGbp === "yes") google += 5;
  if (d.hasSeo === "yes") google += 4;
  if (d.reviewRange === "100-plus") google += 4;
  else if (d.reviewRange === "50-100") google += 3;
  else if (d.reviewRange === "10-50") google += 2;
  else if (d.reviewRange === "under-10") google += 1;

  // Paid Ads (0-20)
  if (d.runningGoogle === "yes") paidAds += 8;
  if (d.runningMeta === "yes") paidAds += 6;
  if (["5k-10k", "10k-plus"].includes(d.googleBudget)) paidAds += 3;
  else if (d.googleBudget === "2k-5k") paidAds += 2;
  if (["5k-10k", "10k-plus"].includes(d.metaBudget)) paidAds += 3;
  else if (d.metaBudget === "2k-5k") paidAds += 2;

  // Social / Content (0-10)
  if (d.runningMeta === "yes") social += 5;
  if (d.hasDirectMail === "yes") social += 5;

  // Tracking & Follow-Up (0-20)
  if (d.tracksCalls === "yes") tracking += 10;
  if (d.tracksShowRate === "yes") tracking += 10;

  // Budget vs Production (0-10)
  const highCollections = ["1m-1.5m", "1.5m-3m", "3m-plus"].includes(d.collections);
  const midCollections = d.collections === "750k-1m";
  const highBudget = ["5k-10k", "10k-plus"].includes(d.monthlyBudget);
  if (highCollections && highBudget) budgetVsProduction = 10;
  else if (highCollections) budgetVsProduction = 7;
  else if (midCollections && highBudget) budgetVsProduction = 6;
  else if (midCollections) budgetVsProduction = 4;
  else budgetVsProduction = 2;

  const total = Math.min(website + google + paidAds + social + tracking + budgetVsProduction, 100);

  let grade: string;
  if (total >= 80) grade = "A";
  else if (total >= 60) grade = "B";
  else if (total >= 40) grade = "C";
  else grade = "D";

  return { website, google, paidAds, social, tracking, budgetVsProduction, total, grade };
}

/* ---------- component ---------- */
const TOTAL_STEPS = 4;

const GrowthAnalysisForm = () => {
  const [form, setForm] = useState<FormData>(initial);
  const [step, setStep] = useState(1);
  const [result, setResult] = useState<ScoreBreakdown | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const set = <K extends keyof FormData>(k: K, v: FormData[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const score = scoreApplication(form);
    setSubmitting(true);

    try {
      const { error } = await supabase.functions.invoke("ghl-webhook", {
        body: { ...form, growthScore: score.total, growthGrade: score.grade },
      });
      if (error) console.error("GHL webhook error:", error);
    } catch (err) {
      console.error("Failed to send to GHL:", err);
    } finally {
      setSubmitting(false);
    }

    // Navigate to thank-you page (pixel fires there)
    navigate("/apply/thank-you", {
      state: { score, email: form.email },
    });
  };

  /* ---------- recommendation engine ---------- */
  const getRecommendations = (score: ScoreBreakdown, data: FormData) => {
    const recs: { category: string; icon: typeof AlertTriangle; color: string; items: string[] }[] = [];

    // Website
    if (score.website < 15) {
      const items: string[] = [];
      if (data.hasWebsite !== "yes") items.push("You need a modern, mobile-first website — it's the foundation of every growth channel.");
      if (data.tracksConversions !== "yes") items.push("Without conversion tracking, you're flying blind. You can't optimize what you don't measure.");
      if (items.length) recs.push({ category: "Website", icon: AlertTriangle, color: "text-red-400", items });
    }

    // Google
    if (score.google < 14) {
      const items: string[] = [];
      if (data.hasGbp !== "yes") items.push("Claim and optimize your Google Business Profile — it's free and drives local patient discovery.");
      if (data.postsOnGbp !== "yes") items.push("Posting weekly on your Google profile boosts local visibility and signals activity to Google.");
      if (data.hasSeo !== "yes") items.push("SEO compounds over time. Without it, you're paying for every single patient click.");
      if (["under-10", "10-50"].includes(data.reviewRange)) items.push("You need more Google reviews. Practices with 100+ reviews dominate local search results.");
      if (items.length) recs.push({ category: "Google / SEO / Maps", icon: AlertTriangle, color: "text-orange-400", items });
    }

    // Paid Ads
    if (score.paidAds < 14) {
      const items: string[] = [];
      if (data.runningGoogle !== "yes") items.push("Google Ads puts you in front of patients actively searching for a dentist — highest intent channel available.");
      if (data.runningMeta !== "yes") items.push("Meta Ads (Facebook/Instagram) build awareness and fill your pipeline before patients even start searching.");
      if (items.length) recs.push({ category: "Paid Advertising", icon: AlertTriangle, color: "text-orange-400", items });
    }

    // Tracking
    if (score.tracking < 14) {
      const items: string[] = [];
      if (data.tracksCalls !== "yes") items.push("Call tracking reveals which campaigns actually produce phone calls — without it, you're guessing where to spend.");
      if (data.tracksShowRate !== "yes") items.push("Tracking show rates exposes your biggest hidden leak. Getting leads is useless if they don't show up.");
      if (items.length) recs.push({ category: "Tracking & Follow-Up", icon: AlertTriangle, color: "text-red-400", items });
    }

    // Budget
    if (score.budgetVsProduction < 7) {
      recs.push({
        category: "Budget vs Production",
        icon: AlertTriangle,
        color: "text-yellow-400",
        items: ["Your marketing investment may not be aligned with your production goals. Practices typically need to invest 5–10% of target revenue in marketing to hit growth targets."],
      });
    }

    return recs;
  };

  /* ---------- RESULT PAGE ---------- */
  if (result) {
    const categories = [
      { label: "Website", score: result.website, max: 20 },
      { label: "Google / SEO / Maps", score: result.google, max: 20 },
      { label: "Paid Ads", score: result.paidAds, max: 20 },
      { label: "Social & Content", score: result.social, max: 10 },
      { label: "Tracking & Follow-Up", score: result.tracking, max: 20 },
      { label: "Budget vs Production", score: result.budgetVsProduction, max: 10 },
    ];

    const recommendations = getRecommendations(result, form);
    const isQualified = result.grade === "A" || result.grade === "B";

    return (
      <section id="growth-analysis" className="py-16 md:py-20 bg-hero-bg">
        <div className="max-w-2xl mx-auto section-padding">
          <div className="text-center mb-8">
            <BarChart3 className="w-12 h-12 text-accent mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold text-hero-foreground mb-2">
              Your Practice Growth Score
            </h2>
            <div className="flex items-center justify-center gap-4 mb-4">
              <span className="text-6xl font-bold text-accent">{result.total}</span>
              <span className="text-3xl font-bold text-hero-foreground/40">/100</span>
              <span className={`text-2xl font-bold px-4 py-1 rounded-lg ${
                result.grade === "A" ? "bg-green-500/20 text-green-400" :
                result.grade === "B" ? "bg-yellow-500/20 text-yellow-400" :
                result.grade === "C" ? "bg-orange-500/20 text-orange-400" :
                "bg-red-500/20 text-red-400"
              }`}>
                Grade {result.grade}
              </span>
            </div>
            <p className="text-hero-foreground/60 text-sm">
              {result.total >= 80 ? "Strong — you're ahead of most practices" :
               result.total >= 60 ? "Stable — but you're leaking patients in key areas" :
               result.total >= 40 ? "Underperforming — significant growth opportunities exist" :
               "Broken system — you're leaving serious money on the table"}
            </p>
          </div>

          {/* Category breakdown */}
          <div className="bg-card rounded-2xl p-6 md:p-8 border border-border shadow-lg mb-6">
            <p className="font-semibold text-foreground mb-4">Score Breakdown</p>
            <div className="space-y-4">
              {categories.map((cat) => {
                const pct = Math.round((cat.score / cat.max) * 100);
                return (
                  <div key={cat.label}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-foreground/80">{cat.label}</span>
                      <span className="font-semibold text-foreground">{cat.score}/{cat.max}</span>
                    </div>
                    <div className="h-2.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-700 ${
                          pct >= 70 ? "bg-green-500" : pct >= 40 ? "bg-yellow-500" : "bg-accent"
                        }`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Actionable recommendations */}
          {recommendations.length > 0 && (
            <div className="bg-card rounded-2xl p-6 md:p-8 border border-border shadow-lg mb-6">
              <p className="font-semibold text-foreground mb-1">Where You're Losing Patients</p>
              <p className="text-muted-foreground text-sm mb-5">These are the gaps holding your practice back from its next level of growth.</p>
              <div className="space-y-5">
                {recommendations.map((rec) => (
                  <div key={rec.category}>
                    <div className="flex items-center gap-2 mb-2">
                      <rec.icon className={`w-4 h-4 ${rec.color}`} />
                      <span className="font-semibold text-foreground text-sm">{rec.category}</span>
                    </div>
                    <ul className="space-y-2 ml-6">
                      {rec.items.map((item, i) => (
                        <li key={i} className="text-sm text-muted-foreground leading-relaxed list-disc">{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 90-day roadmap notice */}
          <div className="bg-card rounded-2xl p-6 md:p-8 border border-border shadow-lg mb-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-accent/15 flex items-center justify-center shrink-0">
                <Clock className="w-5 h-5 text-accent" />
              </div>
              <div>
                <p className="font-bold text-foreground mb-1">Your 90-Day Growth Roadmap Is on the Way</p>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  We're building a personalized 90-day action plan based on your answers. It will be sent to{" "}
                  <span className="font-semibold text-accent">{form.email}</span> shortly — including the exact steps to close the gaps above and start adding new patients.
                </p>
              </div>
            </div>
          </div>

          {/* Next steps */}
          <div className="bg-card rounded-2xl p-6 md:p-8 border border-border shadow-lg text-center">
            {isQualified ? (
              <>
                <TrendingUp className="w-10 h-10 text-accent mx-auto mb-3" />
                <h3 className="text-lg font-bold text-foreground mb-2">
                  Want to Fix These Gaps Faster?
                </h3>
                <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
                  Your roadmap will give you the what. On a Growth Call, we'll walk through the how — 
                  including exactly which leaks to fix first for the fastest ROI.
                </p>
                <a
                  href="https://api.leadconnectorhq.com/widget/bookings/meetwithhans"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-accent text-accent-foreground px-8 py-4 rounded-lg text-base font-semibold hover:bg-accent/90 transition-all shadow-lg shadow-accent/25"
                >
                  Book Your Growth Call
                  <ArrowRight className="w-4 h-4" />
                </a>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-10 h-10 text-accent mx-auto mb-3" />
                <h3 className="text-lg font-bold text-foreground mb-2">
                  Your Roadmap Is Coming
                </h3>
                <p className="text-muted-foreground text-sm mb-6 leading-relaxed">
                  Check your inbox for your personalized 90-day plan. It covers the exact fixes for each gap above, 
                  plus a free Growth Guide with proven strategies for practices at your stage.
                </p>
                <a
                  href="#"
                  className="inline-flex items-center justify-center gap-2 bg-accent text-accent-foreground px-8 py-4 rounded-lg text-base font-semibold hover:bg-accent/90 transition-all shadow-lg shadow-accent/25"
                >
                  Download the Free Growth Guide
                  <ArrowRight className="w-4 h-4" />
                </a>
              </>
            )}
          </div>

          <p className="text-hero-foreground/30 text-xs text-center mt-4">
            Your information is confidential and never shared.
          </p>
        </div>
      </section>
    );
  }

  /* ---------- FORM ---------- */
  const YesNo = ({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) => (
    <div className="space-y-2">
      <Label>{label}</Label>
      <RadioGroup value={value} onValueChange={onChange} className="flex gap-4">
        <div className="flex items-center gap-2">
          <RadioGroupItem value="yes" id={`${label}-y`} />
          <Label htmlFor={`${label}-y`} className="font-normal cursor-pointer">Yes</Label>
        </div>
        <div className="flex items-center gap-2">
          <RadioGroupItem value="no" id={`${label}-n`} />
          <Label htmlFor={`${label}-n`} className="font-normal cursor-pointer">No</Label>
        </div>
      </RadioGroup>
    </div>
  );

  return (
    <section id="growth-analysis" className="py-16 md:py-20 bg-hero-bg">
      <div className="max-w-2xl mx-auto section-padding">
        <div className="text-center mb-10">
          <p className="text-accent font-semibold text-sm tracking-wide uppercase mb-3">
            Growth Analysis
          </p>
          <h2 className="text-2xl md:text-3xl font-bold text-hero-foreground tracking-tight mb-3">
            Start Your Practice Growth Analysis
          </h2>
          <p className="text-hero-foreground/50 text-sm">
            Takes about 3 minutes. Your score appears instantly.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-card rounded-2xl p-6 md:p-8 border border-border shadow-lg">
          {/* Step indicator */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((s) => (
              <div key={s} className="flex items-center gap-2">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${
                    step >= s ? "bg-accent text-accent-foreground" : "bg-muted text-muted-foreground"
                  }`}
                >
                  {s}
                </div>
                {s < TOTAL_STEPS && <div className="w-8 h-0.5 bg-border" />}
              </div>
            ))}
          </div>

          {/* STEP 1: Practice Basics + Website */}
          {step === 1 && (
            <div className="space-y-5">
              <p className="font-semibold text-foreground text-lg">Practice Basics</p>

              <div className="space-y-1.5">
                <Label>Monthly Collections (approx.) *</Label>
                <Select value={form.collections} onValueChange={(v) => set("collections", v)}>
                  <SelectTrigger><SelectValue placeholder="Select range" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="under-750k">Under $750K / year</SelectItem>
                    <SelectItem value="750k-1m">$750K – $1M / year</SelectItem>
                    <SelectItem value="1m-1.5m">$1M – $1.5M / year</SelectItem>
                    <SelectItem value="1.5m-3m">$1.5M – $3M / year</SelectItem>
                    <SelectItem value="3m-plus">$3M+ / year</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>Primary Goal *</Label>
                <Select value={form.goal} onValueChange={(v) => set("goal", v)}>
                  <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="grow">Grow new patients</SelectItem>
                    <SelectItem value="stabilize">Stabilize current flow</SelectItem>
                    <SelectItem value="expand">Expand locations</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <hr className="border-border my-2" />
              <p className="font-semibold text-foreground text-lg">Website</p>

              <YesNo label="Do you have a website? *" value={form.hasWebsite} onChange={(v) => set("hasWebsite", v)} />

              {form.hasWebsite === "yes" && (
                <div className="space-y-1.5">
                  <Label htmlFor="url">Website URL</Label>
                  <Input id="url" value={form.websiteUrl} onChange={(e) => set("websiteUrl", e.target.value)} placeholder="https://..." />
                </div>
              )}

              <YesNo label="Do you track website conversions? *" value={form.tracksConversions} onChange={(v) => set("tracksConversions", v)} />

              <button
                type="button"
                onClick={() => form.collections && form.goal && form.hasWebsite && setStep(2)}
                className="w-full bg-accent text-accent-foreground py-3 rounded-lg font-semibold hover:bg-accent/90 transition-all"
              >
                Continue →
              </button>
            </div>
          )}

          {/* STEP 2: Google + Ads */}
          {step === 2 && (
            <div className="space-y-5">
              <p className="font-semibold text-foreground text-lg">Google / Local Presence</p>

              <YesNo label="Do you have a Google Business Profile? *" value={form.hasGbp} onChange={(v) => set("hasGbp", v)} />

              <div className="space-y-1.5">
                <Label># of Google Reviews *</Label>
                <Select value={form.reviewRange} onValueChange={(v) => set("reviewRange", v)}>
                  <SelectTrigger><SelectValue placeholder="Select range" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="under-10">Under 10</SelectItem>
                    <SelectItem value="10-50">10 – 50</SelectItem>
                    <SelectItem value="50-100">50 – 100</SelectItem>
                    <SelectItem value="100-plus">100+</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <YesNo label="Do you post on your Google Business Profile? *" value={form.postsOnGbp} onChange={(v) => set("postsOnGbp", v)} />

              <hr className="border-border my-2" />
              <p className="font-semibold text-foreground text-lg">Paid Advertising</p>

              <YesNo label="Running Google Ads? *" value={form.runningGoogle} onChange={(v) => set("runningGoogle", v)} />

              {form.runningGoogle === "yes" && (
                <div className="space-y-1.5">
                  <Label>Google Ads Monthly Budget</Label>
                  <Select value={form.googleBudget} onValueChange={(v) => set("googleBudget", v)}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="under-2k">Under $2K</SelectItem>
                      <SelectItem value="2k-5k">$2K – $5K</SelectItem>
                      <SelectItem value="5k-10k">$5K – $10K</SelectItem>
                      <SelectItem value="10k-plus">$10K+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <YesNo label="Running Facebook / Instagram Ads? *" value={form.runningMeta} onChange={(v) => set("runningMeta", v)} />

              {form.runningMeta === "yes" && (
                <div className="space-y-1.5">
                  <Label>Meta Ads Monthly Budget</Label>
                  <Select value={form.metaBudget} onValueChange={(v) => set("metaBudget", v)}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="under-2k">Under $2K</SelectItem>
                      <SelectItem value="2k-5k">$2K – $5K</SelectItem>
                      <SelectItem value="5k-10k">$5K – $10K</SelectItem>
                      <SelectItem value="10k-plus">$10K+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex gap-3">
                <button type="button" onClick={() => setStep(1)} className="px-6 py-3 rounded-lg font-medium border border-border text-foreground hover:bg-muted transition-all">
                  <ArrowLeft className="w-4 h-4 inline mr-1" />Back
                </button>
                <button
                  type="button"
                  onClick={() => form.hasGbp && form.reviewRange && setStep(3)}
                  className="flex-1 bg-accent text-accent-foreground py-3 rounded-lg font-semibold hover:bg-accent/90 transition-all"
                >
                  Continue →
                </button>
              </div>
            </div>
          )}

          {/* STEP 3: Leads + Tracking + Other */}
          {step === 3 && (
            <div className="space-y-5">
              <p className="font-semibold text-foreground text-lg">Leads & Patients</p>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Monthly Leads (calls + forms) *</Label>
                  <Select value={form.monthlyLeads} onValueChange={(v) => set("monthlyLeads", v)}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="under-20">Under 20</SelectItem>
                      <SelectItem value="20-50">20 – 50</SelectItem>
                      <SelectItem value="50-100">50 – 100</SelectItem>
                      <SelectItem value="100-plus">100+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Monthly New Patients *</Label>
                  <Select value={form.monthlyNewPatients} onValueChange={(v) => set("monthlyNewPatients", v)}>
                    <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="under-10">Under 10</SelectItem>
                      <SelectItem value="10-25">10 – 25</SelectItem>
                      <SelectItem value="25-50">25 – 50</SelectItem>
                      <SelectItem value="50-plus">50+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <hr className="border-border my-2" />
              <p className="font-semibold text-foreground text-lg">Tracking & Conversion</p>

              <YesNo label="Do you track incoming calls? *" value={form.tracksCalls} onChange={(v) => set("tracksCalls", v)} />
              <YesNo label="Do you track patient show rate? *" value={form.tracksShowRate} onChange={(v) => set("tracksShowRate", v)} />

              <hr className="border-border my-2" />
              <p className="font-semibold text-foreground text-lg">Other Channels</p>

              <YesNo label="Using direct mail / postcards?" value={form.hasDirectMail} onChange={(v) => set("hasDirectMail", v)} />
              <YesNo label="Currently running SEO?" value={form.hasSeo} onChange={(v) => set("hasSeo", v)} />

              <div className="flex gap-3">
                <button type="button" onClick={() => setStep(2)} className="px-6 py-3 rounded-lg font-medium border border-border text-foreground hover:bg-muted transition-all">
                  <ArrowLeft className="w-4 h-4 inline mr-1" />Back
                </button>
                <button
                  type="button"
                  onClick={() => form.monthlyLeads && form.monthlyNewPatients && form.tracksCalls && setStep(4)}
                  className="flex-1 bg-accent text-accent-foreground py-3 rounded-lg font-semibold hover:bg-accent/90 transition-all"
                >
                  Continue →
                </button>
              </div>
            </div>
          )}

          {/* STEP 4: Intent + Contact */}
          {step === 4 && (
            <div className="space-y-5">
              <p className="font-semibold text-foreground text-lg">Goals & Contact</p>

              <div className="space-y-2">
                <Label>If you found a system that reliably turns $3K–$6K/month into predictable new‑patient revenue, when would you start? *</Label>
                <RadioGroup value={form.timeline} onValueChange={(v) => set("timeline", v)}>
                  {[
                    { value: "now", label: "Now — I'm ready" },
                    { value: "3-6", label: "In the next 3–6 months" },
                    { value: "6-12", label: "6–12 months" },
                    { value: "curious", label: "Just curious" },
                  ].map((opt) => (
                    <div key={opt.value} className="flex items-center gap-2">
                      <RadioGroupItem value={opt.value} id={`tl-${opt.value}`} />
                      <Label htmlFor={`tl-${opt.value}`} className="font-normal cursor-pointer">{opt.label}</Label>
                    </div>
                  ))}
                </RadioGroup>
              </div>

              <div className="space-y-1.5">
                <Label>Monthly budget for growth (next 6–12 months) *</Label>
                <Select value={form.monthlyBudget} onValueChange={(v) => set("monthlyBudget", v)}>
                  <SelectTrigger><SelectValue placeholder="Select range" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="under-2k">Under $2K</SelectItem>
                    <SelectItem value="2k-5k">$2K – $5K</SelectItem>
                    <SelectItem value="5k-10k">$5K – $10K</SelectItem>
                    <SelectItem value="10k-plus">$10K+</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <YesNo label="Are you the owner / final decision‑maker? *" value={form.isDecisionMaker} onChange={(v) => set("isDecisionMaker", v)} />

              <div className="space-y-1.5">
                <Label htmlFor="whyNow">What made you request this today?</Label>
                <Textarea id="whyNow" value={form.whyNow} onChange={(e) => set("whyNow", e.target.value)} placeholder="Optional" rows={2} />
              </div>

              <hr className="border-border my-2" />
              <p className="font-medium text-foreground">Where should we send your results?</p>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input id="name" value={form.name} onChange={(e) => set("name", e.target.value)} required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="phone">Phone *</Label>
                  <Input id="phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} required />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="email">Email *</Label>
                <Input id="email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required />
              </div>

              <div className="flex gap-3">
                <button type="button" onClick={() => setStep(3)} className="px-6 py-3 rounded-lg font-medium border border-border text-foreground hover:bg-muted transition-all">
                  <ArrowLeft className="w-4 h-4 inline mr-1" />Back
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 bg-accent text-accent-foreground py-3 rounded-lg font-semibold hover:bg-accent/90 transition-all inline-flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Analyzing...</>
                  ) : (
                    <>Get My Growth Score <ArrowRight className="w-4 h-4" /></>
                  )}
                </button>
              </div>
            </div>
          )}
        </form>

        <p className="text-hero-foreground/30 text-xs text-center mt-4">
          Your information is confidential and never shared.
        </p>
      </div>
    </section>
  );
};

export default GrowthAnalysisForm;
